import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import type { JournalEntry, LedgerAccount, PlanGroup } from "../../entities/ledger/types";
import { humanError } from "../../shared/api/client";
import { money, payeeName } from "../../shared/lib/format";
import { rankedFilter } from "../../shared/lib/search";
import { Modal } from "../../shared/ui/Modal";
import { assignTransaction, planningMonth } from "./api";
import { listTransactions } from "../transactions/api";

interface GroupAssignDialogProps {
  open: boolean;
  onClose: () => void;
  bookID: string;
  group: PlanGroup;
  accounts: LedgerAccount[];
  onNotify: (message: string) => void;
  onChanged: () => Promise<void>;
}

// Assigning straight to the composite: the transaction belongs to the move,
// the renovation, the month of income, without anyone claiming to know which
// of its items it paid for. Nothing is distributed to the children.
export function GroupAssignDialog({ open, onClose, bookID, group, accounts, onNotify, onChanged }: GroupAssignDialogProps) {
  const queryClient = useQueryClient();
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState<string[]>([]);
  const [failure, setFailure] = useState("");
  const [saving, setSaving] = useState(false);
  const period = planningMonth(0);

  const journal = useQuery({
    queryKey: ["transactions", bookID, period.start, period.end, ""],
    enabled: open && bookID !== "",
    queryFn: () => listTransactions(bookID, period.start, period.end, "", "", 200),
  });

  const accountIDs = useMemo(() => new Set(accounts.map((account) => account.id)), [accounts]);
  const candidates = useMemo(() => (journal.data?.transactions ?? [])
    // Only what has no planning owner: one transaction, one owner.
    .filter((entry: JournalEntry) => !entry.planningAssignment)
    .map((entry: JournalEntry) => {
      const economic = entry.postings.find((posting) => !accountIDs.has(posting.accountId)) ?? entry.postings[0];
      return {
        id: entry.transaction.id,
        label: payeeName(entry.transaction.description || "Transaction"),
        when: new Date(entry.transaction.effectiveAt).toLocaleDateString("en", { day: "2-digit", month: "short" }),
        amount: economic ? money(economic.amount.minor, economic.amount.currency) : "",
      };
    }), [journal.data, accountIDs]);

  const shown = useMemo(
    () => rankedFilter(candidates, query, (candidate) => [candidate.label, candidate.amount]),
    [candidates, query],
  );

  function close() {
    setPicked([]);
    setQuery("");
    setFailure("");
    onClose();
  }

  async function submit() {
    setSaving(true);
    setFailure("");
    let done = 0;
    try {
      for (const id of picked) {
        await assignTransaction(bookID, id, { planGroupId: group.id });
        done += 1;
      }
      await queryClient.invalidateQueries({ queryKey: ["transactions"] });
      await onChanged();
      onNotify(done === 1
        ? `1 transaction now belongs to "${group.name}".`
        : `${done} transactions now belong to "${group.name}".`);
      close();
    } catch (error) {
      await onChanged();
      setFailure(done === 0 ? humanError(error) : `${done} of ${picked.length} were assigned, then it stopped: ${humanError(error)}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open} onClose={close} title="Assign a transaction to this group" subtitle={group.name}
      footer={<>
        <button className="secondary-button" type="button" onClick={close}>Cancel</button>
        <button className="primary-button compact" type="button" disabled={saving || picked.length === 0} onClick={submit}>
          <span>{saving ? "Assigning…" : picked.length > 1 ? `Assign ${picked.length} transactions` : "Assign"}</span>
          <span aria-hidden="true">→</span>
        </button>
      </>}
    >
      <p className="panel-note">
        These transactions will belong to the group as a whole. They are not spread over its plan items, and none of
        those items is marked fulfilled by them.
      </p>
      {journal.isPending ? <p className="panel-note">Loading this month's transactions…</p> : null}
      {journal.isError ? <p className="form-error" role="alert">{humanError(journal.error)}</p> : null}
      {journal.data && candidates.length === 0 ? (
        <p className="panel-note">Every transaction of this month already belongs to a plan item or a group.</p>
      ) : null}
      {candidates.length > 0 ? (
        <>
          <div className="pick-search">
            <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></svg>
            <input type="search" value={query} autoComplete="off" aria-label="Search transactions"
              placeholder={`Search ${candidates.length} unassigned ${candidates.length === 1 ? "transaction" : "transactions"}`}
              onChange={(event) => setQuery(event.target.value)} />
          </div>
          <ul className="pick-list">
            {shown.map((candidate) => (
              <li key={candidate.id} className={`pick-row ${picked.includes(candidate.id) ? "picked" : ""}`}>
                <label className="pick-main">
                  <input type="checkbox" checked={picked.includes(candidate.id)}
                    onChange={() => setPicked((current) => current.includes(candidate.id)
                      ? current.filter((value) => value !== candidate.id)
                      : [...current, candidate.id])} />
                  <span className="pick-copy">
                    <strong title={candidate.label}>{candidate.label}</strong>
                    <small>{candidate.when}</small>
                  </span>
                  <span className="pick-amount">{candidate.amount}</span>
                </label>
              </li>
            ))}
            {shown.length === 0 ? <li className="pick-empty">Nothing matches “{query.trim()}”.</li> : null}
          </ul>
        </>
      ) : null}
      {failure ? <p className="form-error" role="alert">{failure}</p> : null}
    </Modal>
  );
}
